package com.eladfinish.bible.contest.screens.browse.tableOfContents.ui

import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ArrowBack
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.ListItem
import androidx.compose.material3.ListItemDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.parashat_hashavua_placeholder
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabAction
import com.eladfinish.bible.contest.screens.browse.tableOfContents.BrowseTabUiState
import com.eladfinish.bible.contest.ui.common.AnimatedColumn
import com.eladfinish.bible.contest.ui.components.buttons.ItemButtonText
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList

@Composable
fun BrowseTabRoot(
    browseTabActionDispatcher: (BrowseTabAction) -> Unit,
    browseTabUiState: BrowseTabUiState,
) {
    BrowseTabScreen(
        browseTabUiState = browseTabUiState,
        onCurrentParashaClick = {
            browseTabActionDispatcher(BrowseTabAction.CurrentParashaClicked)
        },
        onBookClick = {
            browseTabActionDispatcher(BrowseTabAction.BookClicked(index = it, longClick = false))

        },
        onBookLongClick = {
            browseTabActionDispatcher(BrowseTabAction.BookClicked(index = it, longClick = true))

        },
        onChapterOrParashaClick = {
            browseTabActionDispatcher(BrowseTabAction.PartClicked(index = it))
        },
    )
}

@Composable
internal fun BrowseTabScreen(
    browseTabUiState: BrowseTabUiState,
    onBookClick: (index: Int) -> Unit,
    onBookLongClick: (index: Int) -> Unit,
    onChapterOrParashaClick: (index: Int) -> Unit,
    onCurrentParashaClick: () -> Unit,
) {
    val indexOfPickedBook = browseTabUiState.indexOfPickedBook
    val secondaryList = browseTabUiState.secondaryList
    val bookList = browseTabUiState.bookList
    val bookColorList = browseTabUiState.bookColorList
    val currentParasha = browseTabUiState.currentParasha.name

    Column {
        BooksAndChaptersContainer(
            bookList = bookList,
            bookColorList = bookColorList,
            indexOfPickedBook = indexOfPickedBook,
            secondaryList = secondaryList,
            shouldPrefixChapterByCheckmark = browseTabUiState.shouldPrefixChapterByCheckmark,
            hasSecondaryList = browseTabUiState.hasSecondaryList,
            isListLastOnScreen = !browseTabUiState.shouldShowCurrentParasha,
            onBookClick = onBookClick,
            onBookLongClick = onBookLongClick,
            onChapterOrParashaClick = onChapterOrParashaClick,
        )

        if (browseTabUiState.shouldShowCurrentParasha) {
            ItemButtonText(
                modifier = Modifier
                    .fillMaxWidth()
                    .navigationBarInsetPadding()
                    .padding(5.dp),
                text = stringResource(SharedRes.strings.parashat_hashavua_placeholder, currentParasha),
                borderWidth = 0,
                onClick = { onCurrentParashaClick() },
            )
        }
    }
}

@Composable
private fun ColumnScope.BooksAndChaptersContainer(
    bookList: List<StringDesc>,
    bookColorList: List<Color>,
    indexOfPickedBook: Int,
    secondaryList: ImmutableList<Pair<StringDesc, Boolean>>,
    shouldPrefixChapterByCheckmark: Boolean,
    hasSecondaryList: Boolean,
    isListLastOnScreen: Boolean,
    onBookClick: (index: Int) -> Unit,
    onBookLongClick: (index: Int) -> Unit,
    onChapterOrParashaClick: (index: Int) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxSize()
            .weight(1f)
            .padding(4.dp),
    ) {
        this@BooksAndChaptersContainer.BooksContainer(
            bookList = bookList,
            bookColorList = bookColorList,
            indexOfPickedBook = indexOfPickedBook,
            isListLastOnScreen = isListLastOnScreen,
            onBookClick = { onBookClick(it) },
            onBookLongClick = { onBookLongClick(it) },
        )

        if (hasSecondaryList) {
            AnimatedColumn(
                targetState = secondaryList,
                modifier = Modifier.weight(1f),
                content = {
                    ChaptersContainer(
                        secondaryList = it,
                        shouldPrefixChapterByCheckmark = shouldPrefixChapterByCheckmark,
                        isListLastOnScreen = isListLastOnScreen,
                        onParashaClick = { index -> onChapterOrParashaClick(index) },
                    )
                },
            )
        }
    }
}

@Composable
private fun ColumnScope.BooksContainer(
    bookList: List<StringDesc>,
    bookColorList: List<Color>,
    indexOfPickedBook: Int,
    isListLastOnScreen: Boolean,
    onBookClick: (Int) -> Unit,
    onBookLongClick: (Int) -> Unit,
) {
    val listState = rememberLazyListState()

    LazyColumn(
        state = listState,
        modifier = Modifier
            .weight(1f)
            .mouseVerticalScrollbar(listState),
        contentPadding = listContentPadding(isListLastOnScreen),
        verticalArrangement = Arrangement.spacedBy(0.dp),
    ) {
        itemsIndexed(bookList) { index, book ->
            val decorated = index == indexOfPickedBook
            ListItem(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(2.dp)
                    .combinedClickable(
                        onClick = { onBookClick(index) },
                        onLongClick = { onBookLongClick(index) },
                    ),
                headlineContent = {
                    Text(
                        text = book.localized(),
                        style = customTextStyle(decorated = decorated),
                        fontWeight = FontWeight.Bold.takeIf { decorated },
                        color = bookColorList[index],
                    )
                },
                trailingContent = {
                    if (decorated) {
                        @Suppress("DEPRECATION")
                        Icon(
                            imageVector = Icons.Outlined.ArrowBack,
                            contentDescription = null,
                        )
                    }
                },
                colors = ListItemDefaults.colors(containerColor = Color.Transparent),
            )
            if (index < bookList.lastIndex) {
                CustomDivider()
            }
        }
    }
}

@Composable
private fun ChaptersContainer(
    secondaryList: ImmutableList<Pair<StringDesc, Boolean>>,
    shouldPrefixChapterByCheckmark: Boolean,
    isListLastOnScreen: Boolean,
    onParashaClick: (Int) -> Unit,
) {
    val listState = rememberLazyListState()

    LazyColumn(
        state = listState,
        modifier = Modifier.mouseVerticalScrollbar(listState),
        contentPadding = listContentPadding(isListLastOnScreen),
    ) {
        itemsIndexed(secondaryList) { index, chapter ->
            ListItem(
                modifier = Modifier.combinedClickable(
                    onClick = { onParashaClick(index) },
                ),
                headlineContent = {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        if (shouldPrefixChapterByCheckmark) {
                            Box(
                                modifier = Modifier
                                    .size(24.dp),
                                contentAlignment = Alignment.Center,
                            ) {
                                if (chapter.second) {
                                    Text(
                                        text = "✓",
                                        style = customTextStyle(decorated = false),
                                    )
                                }
                            }
                        }

                        Text(
                            text = chapter.first.localized(),
                            style = customTextStyle(decorated = false),
                        )
                    }
                },
                colors = ListItemDefaults.colors(containerColor = Color.Transparent),
            )

            if (index < secondaryList.lastIndex) {
                CustomDivider()
            }
        }
    }
}


/** The lists end above the current-parasha button when it shows, and the button carries the navigation bar inset. */
@Composable
private fun listContentPadding(isListLastOnScreen: Boolean): PaddingValues =
    if (isListLastOnScreen) navigationBarContentPadding() else PaddingValues()

@Composable
private fun customTextStyle(decorated: Boolean): TextStyle =
    MaterialTheme.typography.bodyMedium.copy(
        color = Color.Black,
        fontSize = 16.sp,
        textDecoration = TextDecoration.Underline.takeIf { decorated },
    )

private val DIVIDER_COLOR = Color.Gray.copy(alpha = 0.25f)

@Composable
private fun CustomDivider() {
    HorizontalDivider(
        thickness = 2.dp,
        color = DIVIDER_COLOR
    )
}
